import { PLOTLY_THEME_DEFAULTS, AXIS_LAYOUT_KEY_PATTERN } from "./constants"

const MUI_PALETTE_VARS = {
  textPrimary: "--mui-palette-text-primary",
  textSecondary: "--mui-palette-text-secondary",
  divider: "--mui-palette-divider",
  backgroundPaper: "--mui-palette-background-paper",
} as const

type PlainObject = Record<string, unknown>

function isPlainObject(value: unknown): value is PlainObject {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

function getCssVariableValue(
  root: Element | null | undefined,
  variableName: string,
  fallbackValue: string,
): string {
  if (!root) {
    return fallbackValue
  }

  const computedValue = root.ownerDocument?.defaultView
    ?.getComputedStyle(root)
    .getPropertyValue(variableName)
    .trim()

  return computedValue || fallbackValue
}

function deepMerge(base: unknown, overrides: unknown): unknown {
  if (!isPlainObject(base)) {
    return overrides
  }

  if (!isPlainObject(overrides)) {
    return base
  }

  const merged: PlainObject = { ...base }

  Object.keys(overrides).forEach((key) => {
    const overrideValue = overrides[key]
    const baseValue = merged[key]
    merged[key] = isPlainObject(baseValue) && isPlainObject(overrideValue)
      ? deepMerge(baseValue, overrideValue)
      : overrideValue
  })

  return merged
}

function normalizePlotlyTitle(title: unknown): unknown {
  if (typeof title === "string") {
    return { text: title }
  }

  return title
}

function normalizePlotlyLayout(layout: unknown): PlainObject {
  const sourceLayout = isPlainObject(layout) ? layout : {}

  const normalizedLayout: PlainObject = { ...sourceLayout }
  if (sourceLayout.title !== undefined) {
    normalizedLayout.title = normalizePlotlyTitle(sourceLayout.title)
  }

  Object.keys(sourceLayout).forEach((key) => {
    const axisLayout = sourceLayout[key]
    if (!AXIS_LAYOUT_KEY_PATTERN.test(key) || !isPlainObject(axisLayout)) {
      return
    }

    const normalizedAxis: PlainObject = { ...axisLayout }
    if (axisLayout.title !== undefined) {
      normalizedAxis.title = normalizePlotlyTitle(axisLayout.title)
    }
    normalizedLayout[key] = normalizedAxis
  })

  return normalizedLayout
}

function createAxisTheme(textColor: string, secondaryTextColor: string, dividerColor: string): PlainObject {
  return {
    tickfont: { color: textColor },
    title: { font: { color: textColor } },
    gridcolor: dividerColor,
    zerolinecolor: secondaryTextColor,
    linecolor: dividerColor,
  }
}

export interface PlotlyLayoutColors {
  textColor: string
  secondaryTextColor: string
  dividerColor: string
  paperColor: string
}

function createThemeLayout(
  container: Element | null | undefined,
  layoutColors?: PlotlyLayoutColors | null,
): PlainObject {
  const root = container?.ownerDocument?.documentElement

  const textColor =
    layoutColors?.textColor
    ?? getCssVariableValue(root, MUI_PALETTE_VARS.textPrimary, PLOTLY_THEME_DEFAULTS.textColor)
  const secondaryTextColor =
    layoutColors?.secondaryTextColor
    ?? getCssVariableValue(root, MUI_PALETTE_VARS.textSecondary, PLOTLY_THEME_DEFAULTS.textSecondaryColor)
  const dividerColor =
    layoutColors?.dividerColor
    ?? getCssVariableValue(root, MUI_PALETTE_VARS.divider, PLOTLY_THEME_DEFAULTS.dividerColor)
  const paperColor =
    layoutColors?.paperColor
    ?? getCssVariableValue(root, MUI_PALETTE_VARS.backgroundPaper, PLOTLY_THEME_DEFAULTS.bgColor)

  return {
    paper_bgcolor: paperColor,
    plot_bgcolor: paperColor,
    font: { color: textColor },
    title: { font: { color: textColor } },
    xaxis: createAxisTheme(textColor, secondaryTextColor, dividerColor),
    yaxis: createAxisTheme(textColor, secondaryTextColor, dividerColor),
    legend: {
      bgcolor: paperColor,
      bordercolor: dividerColor,
      font: { color: textColor },
    },
    modebar: {
      bgcolor: paperColor,
      color: secondaryTextColor,
      activecolor: textColor,
    },
    hoverlabel: {
      bgcolor: paperColor,
      bordercolor: dividerColor,
      font: { color: textColor },
    },
  }
}

const PRIMARY_AXIS_KEYS = new Set(["xaxis", "yaxis"])

function applyAxisThemeToLayout(layout: unknown, axisTheme: unknown): unknown {
  if (!isPlainObject(layout)) {
    return layout
  }

  const theme = isPlainObject(axisTheme) ? axisTheme : null
  if (!theme) {
    return layout
  }

  const nextLayout: PlainObject = { ...layout }

  Object.keys(layout).forEach((key) => {
    const axisLayout = layout[key]
    const isAxisKey = AXIS_LAYOUT_KEY_PATTERN.test(key) && isPlainObject(axisLayout)
    const isExtraAxis = isAxisKey && !PRIMARY_AXIS_KEYS.has(key)
    if (isExtraAxis) {
      nextLayout[key] = deepMerge(theme, axisLayout)
    }
  })

  return nextLayout
}

interface ThemeLike {
  palette?: {
    text?: { primary?: string, secondary?: string }
    background?: { paper?: string, default?: string }
    divider?: string
  }
}

function getPlotlyLayoutColorsFromTheme(theme: ThemeLike | null | undefined): PlotlyLayoutColors | null {
  const p = theme?.palette
  if (!p?.text?.primary) return null
  return {
    textColor: p.text.primary,
    secondaryTextColor: p.text.secondary ?? p.text.primary,
    dividerColor: p.divider ?? PLOTLY_THEME_DEFAULTS.dividerColor,
    paperColor: p.background?.paper ?? PLOTLY_THEME_DEFAULTS.bgColor,
  }
}

export {
  applyAxisThemeToLayout,
  createThemeLayout,
  deepMerge,
  getPlotlyLayoutColorsFromTheme,
  normalizePlotlyLayout,
}
